/**
 * OperatorTable — generic structured table for Direction A.
 *
 * Features:
 *   - Optional checkbox column
 *   - Column config (key, header, render, align, width)
 *   - Title slot in card header + Filter / Sort / Columns chips
 *   - Stage pill + Helix mini-bar helpers exported for use in render fns
 */
import { tokens, getStageStyle } from "./tokens";
import { getHealthColor } from "@shared/schema";

export interface OperatorColumn<T> {
  key: string;
  header: string;
  render: (row: T, index: number) => React.ReactNode;
  align?: "left" | "right" | "center";
  width?: number | string;
  numeric?: boolean;
}

export interface OperatorTableProps<T> {
  title?: string;
  columns: OperatorColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string | number;
  showCheckbox?: boolean;
  onRowClick?: (row: T) => void;
  toolbar?: React.ReactNode;       // custom toolbar override
  showDefaultChips?: boolean;      // shows Filter / Sort / Columns chips when true
  emptyMessage?: string;
}

export function StagePill({ stage }: { stage: string }) {
  const s = getStageStyle(stage);
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 10px",
        borderRadius: 999,
        background: s.bg,
        color: s.fg,
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1.4,
        border: s.dashed ? `1px dashed ${s.fg}` : "1px solid transparent",
        fontFamily: tokens.font.body,
      }}
    >
      {stage}
    </span>
  );
}

export function HelixBar({ score, max = 100 }: { score: number; max?: number }) {
  const pct = Math.max(0, Math.min(100, (score / max) * 100));
  // Score-driven bar color routes through canon palette per CANON.md §5.
  const color = getHealthColor(score);
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontVariantNumeric: "tabular-nums", fontSize: 13, fontWeight: 600, color: tokens.color.textPrimary, minWidth: 24 }}>
        {score}
      </span>
      <div style={{ width: 56, height: 4, background: "#EFEFEE", borderRadius: 999, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color }} />
      </div>
    </div>
  );
}

export function OwnerPill({ name, showName = true }: { name: string; showName?: boolean }) {
  const cleaned = (name || "").replace(/^(BDR|AE) · /, "");
  const initials = cleaned
    .split(/\s+/)
    .map((s) => s.replace(/[^A-Za-z]/g, "")[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase() || "—";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: tokens.color.textMuted, fontSize: 13 }}>
      <span
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 22, height: 22, borderRadius: "50%",
          background: "#E5E5E7",
          fontSize: 9, fontWeight: 700, color: tokens.color.black,
          fontFamily: tokens.font.body,
          flexShrink: 0,
        }}
      >
        {initials}
      </span>
      {showName && <span style={{ whiteSpace: "nowrap" }}>{cleaned}</span>}
    </span>
  );
}

function ChipButton({ children }: { children: React.ReactNode }) {
  return (
    <button
      type="button"
      style={{
        height: 28,
        padding: "0 10px",
        background: "#FFFFFF",
        border: `1px solid ${tokens.color.border}`,
        borderRadius: 6,
        fontSize: 12,
        color: tokens.color.textPrimary,
        cursor: "pointer",
        fontFamily: tokens.font.body,
      }}
    >
      {children}
    </button>
  );
}

export default function OperatorTable<T>({
  title,
  columns,
  rows,
  rowKey,
  showCheckbox = true,
  onRowClick,
  toolbar,
  showDefaultChips = true,
  emptyMessage = "No records.",
}: OperatorTableProps<T>) {
  return (
    <div
      style={{
        background: "#FFFFFF",
        border: `1px solid ${tokens.color.border}`,
        borderRadius: tokens.radius.card,
        fontFamily: tokens.font.body,
        overflow: "hidden",
      }}
    >
      {(title || toolbar || showDefaultChips) && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 16px",
            borderBottom: `1px solid ${tokens.color.border}`,
          }}
        >
          {title && (
            <div style={{ fontSize: 14, fontWeight: 600, color: tokens.color.textPrimary }}>
              {title}
            </div>
          )}
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {toolbar}
            {!toolbar && showDefaultChips && (
              <>
                <ChipButton>Filter ▾</ChipButton>
                <ChipButton>Sort ▾</ChipButton>
                <ChipButton>Columns ▾</ChipButton>
              </>
            )}
          </div>
        </div>
      )}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
          <thead>
            <tr style={{ background: "#FAFAF7" }}>
              {showCheckbox && (
                <th style={{ width: 36, padding: "6px 0 6px 14px", textAlign: "left" }}>
                  <input type="checkbox" />
                </th>
              )}
              {columns.map((col) => (
                <th
                  key={col.key}
                  style={{
                    textAlign: col.align ?? (col.numeric ? "right" : "left"),
                    padding: "6px 10px",
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: tokens.color.textMuted,
                    width: col.width,
                    borderBottom: `1px solid ${tokens.color.border}`,
                    whiteSpace: "nowrap",
                  }}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (showCheckbox ? 1 : 0)}
                  style={{ padding: "20px 16px", textAlign: "center", color: tokens.color.textMuted, fontSize: 13 }}
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              rows.map((row, i) => (
                <tr
                  key={rowKey(row)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  style={{
                    borderBottom: i === rows.length - 1 ? "none" : `1px solid ${tokens.color.borderSoft}`,
                    cursor: onRowClick ? "pointer" : "default",
                    transition: "background 0.1s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#FAFBFC")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  {showCheckbox && (
                    <td style={{ padding: "6px 0 6px 14px" }} onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" />
                    </td>
                  )}
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      style={{
                        textAlign: col.align ?? (col.numeric ? "right" : "left"),
                        padding: "6px 10px",
                        color: tokens.color.textPrimary,
                        fontVariantNumeric: col.numeric ? "tabular-nums" : "normal",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {col.render(row, i)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
